import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { employeeService } from "../../services/employeeService";
import DeleteConfirmModal from "../../components/DeleteConfirmModal";
import { toast } from "../../services/toastService";
import Pagination from "../../components/Pagination";
import Loading from "../../components/Loading";

const formatLastSeen = (value) => {
  if (!value) return "Never";

  const diffMs = Date.now() - new Date(`${value}Z`).getTime();
  const diffMinutes = Math.floor(diffMs / 60000);

  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes} min ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} hr ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
};

const Employees = () => {
  const [data, setData] = useState([]);
  const [deleteId, setDeleteId] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (pageNumber = 1) => {
    try {
      setLoading(true);
      const res = await employeeService.getAll({
        pageNumber: pageNumber,
        pageSize: 15,
        orderBy: "id",
        orderDirection: "desc",
      });
      setData(res.value);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const deleteAsync = async () => {
    try {
      setLoading(true);
      if (!deleteId) {
        toast.error("Please select a record to delete");
        return;
      }
      var result = await employeeService.delete(deleteId);
      if (!result.isSuccess) {
        toast.error(result.errors[0] || "Failed to delete record");
        return;
      }
      toast.success("Record deleted successfully");
      setDeleteId(0);
      if (!search) loadData(data.pageNumber);
      else getAllSearchAsync(data.pageNumber);
    } catch (error) {
      toast.error("An error occurred while deleting the record");
    } finally {
      setLoading(false);
    }
  };

  const getAllSearchAsync = async (pageNumber = 1, signal) => {
    try {
      var res = await employeeService.getAllSearchAsync(
        {
          search: search,
          pageNumber: pageNumber,
          pageSize: 15,
          orderBy: "id",
          orderDirection: "desc",
        },
        signal
      );
      setData(res.value);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (search.trim().length < 2) {
      loadData(1);
      return;
    }

    const controller = new AbortController();
    const delay = setTimeout(() => {
      getAllSearchAsync(1, controller.signal);
    }, 400);

    return () => {
      controller.abort();
      clearTimeout(delay);
    };
  }, [search]);

  const onPageChange = async (pageNumber) => {
    setLoading(true);
    if (!search) {
      loadData(pageNumber);
      return;
    }
    await getAllSearchAsync(pageNumber);
  };

  const toggleStatusAsync = async (record) => {
    try {
      setLoading(true);
      const result = await employeeService.changeStatus(record.id, !record.isActive);
      if (!result.isSuccess) {
        toast.error(result.errors[0] || "Failed to update status");
        return;
      }
      toast.success("Status updated successfully");
      if (!search) loadData(data.pageNumber);
      else getAllSearchAsync(data.pageNumber);
    } catch (error) {
      toast.error("An error occurred while updating the status");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Loading />}
      <section className="section-documents">
        <div className="my-card">
          <div className="my-card-header">
            <div className="my-card-title">Employees</div>
          </div>
          <div className="my-card-body">
            <div className="my-grid">
              <div className="my-grid-header">
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                  className="form-control my-grid-search"
                />
              </div>
              <div className="my-grid-body">
                <table className="table table-bordered table-responsive">
                  <thead>
                    <tr>
                      <th>Full Name</th>
                      <th>Email</th>
                      <th>GPS Consent</th>
                      <th>Last Seen</th>
                      <th>Status</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items?.map((record) => (
                      <tr key={record.id}>
                        <td>{record.fullName}</td>
                        <td>{record.email}</td>
                        <td>
                          {record.gpsTrackingEnabled ? (
                            <span className="badge bg-success">Granted</span>
                          ) : (
                            <span className="badge bg-secondary">Not granted</span>
                          )}
                        </td>
                        <td>
                          {formatLastSeen(record.lastLocationAtUtc)}
                          {record.isOnline && (
                            <span className="badge bg-success ms-2">Online</span>
                          )}
                        </td>
                        <td>
                          <div className="form-check form-switch">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              role="switch"
                              checked={!!record.isActive}
                              onChange={() => toggleStatusAsync(record)}
                            />
                          </div>
                        </td>
                        <td className="my-grid-actions">
                          <Link
                            to={`/employees/${record.id}/tracking`}
                            className="fas fa-location-dot text-primary cursor-pointer"
                            title="Track location"
                          />
                          <i
                            className="fas fa-trash text-danger cursor-pointer"
                            data-bs-toggle="modal"
                            data-bs-target="#confirm-modal"
                            onClick={() => setDeleteId(record.id)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="my-grid-footer">
                <Pagination
                  currentPage={data.pageNumber}
                  totalPages={data.totalPages}
                  onPageChange={onPageChange}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      <DeleteConfirmModal deleteAsync={deleteAsync} setDeleteId={setDeleteId} />
    </>
  );
};

export default Employees;
